import * as amplitude from '@amplitude/analytics-browser';
import type {
  BaseEvent,
  EnrichmentPlugin,
  ServerZoneType,
} from '@amplitude/analytics-browser/lib/esm/types';
import type { LoginCompany, WelfarePlan } from '@tundr/auth';
import type { TRACKING_EVENTS } from './tracking-events';
import { ENV_VARS } from '../env';
import { ExternalService, isServiceEnabled } from '../external-services';
import { version } from '../../../../appVersion.json';
import { isInternalHrUserByEmail } from '../../utils/hr/hr.utils';
import { plugin as engagementPlugin } from '@amplitude/engagement-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';

const isAmplitudeEnabled = () =>
  isServiceEnabled(ExternalService.Amplitude) && !!ENV_VARS.amplitude.apiKey;

export enum AuthState {
  ANONYMOUS = 'anonymous',
  AUTHENTICATED = 'authenticated',
  AUTHENTICATED_NO_COMPANY = 'authenticated_no_company',
}

export type TrackingContext = {
  hasSession: boolean;
  company?: LoginCompany | null;
};

type CompanyTrackingProperties = {
  userCompanyId: string;
  userCompanyName: string;
  userCompanyPlan: WelfarePlan;
  userCompanyChannel?: string;
  isInternalHrUser: boolean;
};

const COMPANY_TRACKING_KEYS: ReadonlyArray<keyof CompanyTrackingProperties> = [
  'userCompanyId',
  'userCompanyName',
  'userCompanyPlan',
  'userCompanyChannel',
  'isInternalHrUser',
];

export const isTrackableCompany = (
  company: LoginCompany | null | undefined,
): company is LoginCompany =>
  !!company?.id && !!company?.name?.trim() && !!company?.hrId;

const getTrackableCompany = (context: TrackingContext): LoginCompany | null =>
  context.hasSession && isTrackableCompany(context.company)
    ? context.company
    : null;

const getAuthState = (context: TrackingContext): AuthState => {
  if (!context.hasSession) return AuthState.ANONYMOUS;

  return isTrackableCompany(context.company)
    ? AuthState.AUTHENTICATED
    : AuthState.AUTHENTICATED_NO_COMPANY;
};

const toCompanyTrackingProperties = (
  company: LoginCompany,
): CompanyTrackingProperties => ({
  userCompanyId: company.id,
  userCompanyName: company.name,
  userCompanyPlan: company.currentWelfarePlan,
  userCompanyChannel: company.companyChannel?.name,
  isInternalHrUser: isInternalHrUserByEmail(company.email),
});

// Resolves the company context when each event is sent, instead of when
// Amplitude ingests an identify, so autocaptured and pre-identify events
// carry it too.
export const companyContextPlugin = (
  getContext: () => TrackingContext,
): EnrichmentPlugin => ({
  name: 'tundr-company-context',
  type: 'enrichment',
  execute: async (event) => {
    const context = getContext();
    const company = getTrackableCompany(context);
    const companyProperties = company
      ? toCompanyTrackingProperties(company)
      : null;

    const eventProperties: Record<string, unknown> = {
      ...event.event_properties,
      authState: getAuthState(context),
    };

    for (const key of COMPANY_TRACKING_KEYS) {
      const value = companyProperties?.[key];
      if (value !== undefined) {
        eventProperties[key] = value;
      }
    }

    return { ...event, event_properties: eventProperties };
  },
});

export const identifyTracking = (context: TrackingContext) => {
  if (!isAmplitudeEnabled()) return;

  const company = getTrackableCompany(context);
  const companyProperties = company
    ? toCompanyTrackingProperties(company)
    : null;

  const identify = new amplitude.Identify();
  identify.set('authState', getAuthState(context));

  for (const key of COMPANY_TRACKING_KEYS) {
    const value = companyProperties?.[key];
    if (value === undefined) {
      identify.unset(key);
    } else {
      identify.set(key, value);
    }
  }

  if (company) {
    amplitude.setUserId(company.hrId);
  }

  amplitude.identify(identify);
};

export const resetTracking = () => {
  if (!isAmplitudeEnabled()) return;

  amplitude.reset();
};

export type InitEngagementProps = {
  userId?: string;
  companyId?: string;
  companyName?: string;
  language?: string;
};

const autoCaptureBaseConfig = {
  attribution: false,
  pageViews: false,
  sessions: true,
  formInteractions: true,
  fileDownloads: true,
  elementInteractions: true,
};

export const initAmplitude = (
  extraProps: Record<string, unknown>,
  getContext: () => TrackingContext,
) => {
  if (!isAmplitudeEnabled()) return;

  // Create and Install Session Replay Plugin
  const sessionReplayTracking = sessionReplayPlugin({
    forceSessionTracking: true,
    sampleRate: 1,
  });
  amplitude.add(sessionReplayTracking);

  // Registered before init so the session-start event is enriched too.
  amplitude.add(companyContextPlugin(getContext));

  const props = {
    ...extraProps,
    autocapture: autoCaptureBaseConfig,
    appVersion: version,
    serverZone: 'EU' as ServerZoneType,
  };

  amplitude.init(ENV_VARS.amplitude.apiKey, props);
  amplitude.add(engagementPlugin());
};

/**
 * Initialize Amplitude Engagement for guides and surveys
 * @param props - The properties to initialize Amplitude Engagement
 * @param props.userId - The user ID
 * @param props.companyId - The company ID
 * @param props.companyName - The company name
 * @param props.language - The user's language/locale
 */
export const initAmplitudeEngagement = async (props: InitEngagementProps) => {
  if (!isAmplitudeEnabled()) return;

  if (window.engagement) {
    await window.engagement.boot({
      user: {
        user_id: props.userId,
        user_properties: {
          companyId: props.companyId,
          companyName: props.companyName,
        },
      },
      integrations: [],
    });

    await window.engagement.updateLanguage(props.language ?? 'it-IT');
  }
};

/**
 * Track navigation event (Amplitude specific event)
 * @param navigationEvent - The navigation event
 */
export const trackAmplitudeNavigationEvent = (navigationEvent: BaseEvent) => {
  if (!isAmplitudeEnabled()) return;

  amplitude.track(navigationEvent);
};

/**
 * Track a custom event
 * @param eventName - The name of the event
 * @param eventProperties - The properties of the event
 */
export const trackAmplitudeEvent = (
  eventInput: TRACKING_EVENTS | amplitude.Types.BaseEvent,
  eventProperties?: Record<string, unknown> | undefined,
  eventOptions?: amplitude.Types.EventOptions | undefined,
) => {
  if (!isAmplitudeEnabled()) return;

  amplitude.track(eventInput, eventProperties, eventOptions);
};
